import { Unit, QuantityInfo } from '../stores/appOrderHistoryStore';
import i18n from '../i18n';

// Wine category products use "btl"/"fles" instead of "pcs"/"pc" for piece units
const BOTTLE_CATEGORIES = ['WINE', 'BEER', 'WATER', 'SPIRITS'];

export const getPieceUnitLabel = (category?: string): { short: string; unit: string } => {
  if (category && BOTTLE_CATEGORIES.includes(category)) {
    return { short: i18n.t('common.btl'), unit: i18n.t('common.btl') };
  }
  return { short: 'pcs', unit: 'pieces' };
};

export interface FormattedQuantity {
  value: number;
  unit: string;
  displayText: string;
}

export interface AggregatedQuantityInfo {
  weightedStdAmount: number;
  stdUnit: Unit;
  totalRawQuantity: number;
  hasQuantityData: boolean;
}

const formatNumber = (num: number): string => {
  // Round to 1 decimal place, then remove trailing zeros
  const rounded = Math.round(num * 10) / 10;
  return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
};

export const formatQuantityInfo = (quantity: number, quantityInfo: QuantityInfo, category?: string): FormattedQuantity => {
  const totalQuantity = quantity * (quantityInfo.stdAmount * quantityInfo.stdMultiplier);

  switch (quantityInfo.stdUnit) {
    case Unit.PIECE: {
      const pieceLabel = getPieceUnitLabel(category);
      return {
        value: totalQuantity,
        unit: pieceLabel.unit,
        displayText: `${formatNumber(totalQuantity)} ${pieceLabel.short}`
      };
    }

    case Unit.GRAM:
      if (Math.abs(totalQuantity) >= 1000) {
        const kg = totalQuantity / 1000;
        return {
          value: kg,
          unit: 'kg',
          displayText: `${formatNumber(kg)} kg`
        };
      }
      return {
        value: totalQuantity,
        unit: 'g',
        displayText: `${formatNumber(totalQuantity)} g`
      };

    case Unit.MILLILITER:
      if (Math.abs(totalQuantity) >= 1000) {
        const liters = totalQuantity / 1000;
        return {
          value: liters,
          unit: 'L',
          displayText: `${formatNumber(liters)} L`
        };
      }
      return {
        value: totalQuantity,
        unit: 'ml',
        displayText: `${formatNumber(totalQuantity)} ml`
      };

    default:
      return {
        value: totalQuantity,
        unit: '',
        displayText: `${formatNumber(totalQuantity)}`
      };
  }
};

export const aggregateQuantityInfos = (
  products: Array<{
    rawQuantity: number;
    quantityInfo?: QuantityInfo;
    articleId: string;
    platform: string;
    name?: string;
  }>,
  productKey: string // Should be `${articleId}-${platform}` or `${articleId}-${platform}-${name}` for platforms without articleId
): AggregatedQuantityInfo => {
  if (!products.length) {
    return {
      weightedStdAmount: 0,
      stdUnit: Unit.PIECE,
      totalRawQuantity: 0,
      hasQuantityData: false
    };
  }

  // Filter products with valid quantityInfo
  const productsWithQuantity = products.filter(p => p.quantityInfo);
  
  if (!productsWithQuantity.length) {
    return {
      weightedStdAmount: 0,
      stdUnit: Unit.PIECE,
      totalRawQuantity: products.reduce((sum, p) => sum + p.rawQuantity, 0),
      hasQuantityData: false
    };
  }

  // Group by stdUnit and check for consistency
  const unitGroups = new Map<Unit, typeof productsWithQuantity>();
  productsWithQuantity.forEach(p => {
    const unit = p.quantityInfo!.stdUnit;
    if (!unitGroups.has(unit)) {
      unitGroups.set(unit, []);
    }
    unitGroups.get(unit)!.push(p);
  });

  // If multiple units exist, log warning and use the group with most products
  if (unitGroups.size > 1) {
    console.warn(`Product ${productKey} has mixed units:`, 
      Array.from(unitGroups.keys()).map(unit => `${unit} (${unitGroups.get(unit)!.length} items)`).join(', ')
    );
    
    // Use the unit group with the most products
    const largestGroup = Array.from(unitGroups.entries())
      .sort((a, b) => b[1].length - a[1].length)[0];
    
    const [selectedUnit, selectedProducts] = largestGroup;
    unitGroups.clear();
    unitGroups.set(selectedUnit, selectedProducts);
  }

  const [stdUnit, validProducts] = Array.from(unitGroups.entries())[0];

  // Calculate weighted average stdAmount based on |rawQuantity| so that
  // credit/correction lines that cancel out a prior order (net = 0) don't
  // produce NaN from a divide-by-zero in the weighting.
  const totalAbsRawQuantity = validProducts.reduce((sum, p) => sum + Math.abs(p.rawQuantity), 0);
  const weightedStdAmount = totalAbsRawQuantity > 0
    ? validProducts.reduce((sum, p) => {
        const weight = Math.abs(p.rawQuantity) / totalAbsRawQuantity;
        return sum + (p.quantityInfo!.stdAmount * p.quantityInfo!.stdMultiplier * weight);
      }, 0)
    : 0;

  return {
    weightedStdAmount,
    stdUnit,
    totalRawQuantity: products.reduce((sum, p) => sum + p.rawQuantity, 0),
    hasQuantityData: true
  };
};

export const formatAggregatedQuantity = (
  totalRawQuantity: number,
  aggregatedQuantityInfo: AggregatedQuantityInfo,
  category?: string
): FormattedQuantity | undefined => {
  if (!aggregatedQuantityInfo.hasQuantityData) {
    return undefined;
  }

  const totalQuantity = totalRawQuantity * aggregatedQuantityInfo.weightedStdAmount;

  switch (aggregatedQuantityInfo.stdUnit) {
    case Unit.PIECE: {
      const pieceLabel = getPieceUnitLabel(category);
      return {
        value: totalQuantity,
        unit: pieceLabel.unit,
        displayText: `${formatNumber(totalQuantity)} ${pieceLabel.short}`
      };
    }

    case Unit.GRAM:
      if (Math.abs(totalQuantity) >= 1000) {
        const kg = totalQuantity / 1000;
        return {
          value: kg,
          unit: 'kg',
          displayText: `${formatNumber(kg)} kg`
        };
      }
      return {
        value: totalQuantity,
        unit: 'g',
        displayText: `${formatNumber(totalQuantity)} g`
      };

    case Unit.MILLILITER:
      if (Math.abs(totalQuantity) >= 1000) {
        const liters = totalQuantity / 1000;
        return {
          value: liters,
          unit: 'L',
          displayText: `${formatNumber(liters)} L`
        };
      }
      return {
        value: totalQuantity,
        unit: 'ml',
        displayText: `${formatNumber(totalQuantity)} ml`
      };

    default:
      return {
        value: totalQuantity,
        unit: '',
        displayText: `${formatNumber(totalQuantity)}`
      };
  }
};